import { bunja } from 'bunja';
import { intervalToDuration } from 'date-fns';
import { atom } from 'jotai';

import { overlayBunja } from '@flipster/overlay';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import segment from '@flipster/utils/segment';
import { SparkAuctionEvent } from '@flipster/utils/segment/events/spark-auction';

import BidModal from '@web/app/[locale]/(web)/spark/auction/_bid/BidModal';
import { authGuardBunja } from '@web/hooks/auth-guard/bunja';

import { SparkAuctionIdContext, SparkAuctionIdScope, sparkAuctionBunja, sparkAuctionsBunja } from '../_bunja';

export const rewardImageAreaBunja = bunja(() => {
  const { nowAtom } = bunja.use(sparkAuctionsBunja);
  const { sparkAuctionAtom, auctionDatesAtom } = bunja.use(sparkAuctionBunja);
  const imageUrlAtom = atom((get) => {
    const { titleUrl } = get(sparkAuctionAtom);
    return titleUrl;
  });

  const rewardBlurAtom = atom((get) => {
    const now = get(nowAtom);
    const { displayStart, rewardReveal } = get(auctionDatesAtom);
    const t = getT(now, displayStart, rewardReveal);
    return 1 - t;
  });

  return { imageUrlAtom, rewardBlurAtom };
});

export type Countdown = { type: 'days'; value: number } | { type: 'seconds'; value: number };

export const countdownAreaBunja = bunja(() => {
  const { nowAtom } = bunja.use(sparkAuctionsBunja);
  const { auctionDatesAtom } = bunja.use(sparkAuctionBunja);
  const countdownAtom = atom<Countdown>((get) => {
    const now = get(nowAtom);
    const { end } = get(auctionDatesAtom);
    const diff = Math.max(Number(end) - Number(now), 0);
    const days = Math.trunc(diff / millisecondsInDay);
    if (days > 0) return { type: 'days', value: days };
    return { type: 'seconds', value: Math.trunc(diff / 1000) };
  });

  const countdownMessageAtom = atom((get) => {
    const countdown = get(countdownAtom);
    if (countdown.type === 'days') {
      if (countdown.value === 1) return '1 day';
      return `${countdown.value} days`;
    }
    const { hours = 0, minutes = 0, seconds = 0 } = intervalToDuration({ start: 0, end: countdown.value * 1000 });
    return [hours, minutes, seconds].map((n) => String(n).padStart(2, '0')).join(':');
  });

  return { countdownAtom, countdownMessageAtom };
});

export const bidInfoBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const auctionId = bunja.use(SparkAuctionIdScope);
  const overlay = bunja.use(overlayBunja);
  const { guard } = bunja.use(authGuardBunja);
  const sparkAuctionBunjaInstance = bunja.use(sparkAuctionBunja);
  const countdownAreaBunjaInstance = bunja.use(countdownAreaBunja);
  const { bidInfoAtom, myBidAmountAtom } = sparkAuctionBunjaInstance;
  const { countdownAtom } = countdownAreaBunjaInstance;

  function trackPlaceBidClick(elementType: 'button' | 'textlink') {
    const { highestBidder, myAvailableTotalSparks } = store.get(bidInfoAtom);
    segment.track(SparkAuctionEvent.PlaceBidClick, {
      highest_bid: highestBidder?.amount ?? null,
      available_sparks: myAvailableTotalSparks,
      element_type: elementType,
    });
  }

  function trackIncreaseBidClick() {
    const { highestBidder, myAvailableTotalSparks } = store.get(bidInfoAtom);
    const myBidAmount = store.get(myBidAmountAtom);
    const countdown = store.get(countdownAtom);
    segment.track(SparkAuctionEvent.IncreaseBidClick, {
      highest_bid: highestBidder?.amount ?? null,
      available_sparks: myAvailableTotalSparks,
      current_bid: myBidAmount,
      days_remaining: countdown.type === 'days' ? countdown.value : 0,
    });
  }

  function trackAvailableSparksClick() {
    const { myAvailableTotalSparks } = store.get(bidInfoAtom);
    segment.track(SparkAuctionEvent.AvailableSparksClick, {
      num_sparks: myAvailableTotalSparks,
    });
  }

  const openBidModal = () => {
    overlay.open((close) => (
      <SparkAuctionIdContext value={auctionId}>
        <BidModal onFinish={close} />
      </SparkAuctionIdContext>
    ));
  };

  const openBidModalWithTextLink = guard(() => {
    trackPlaceBidClick('textlink');
    openBidModal();
  });

  const openBidModalWithButton = guard(() => {
    const { isFirstBid } = store.get(bidInfoAtom);
    if (isFirstBid) trackPlaceBidClick('button');
    else trackIncreaseBidClick();

    openBidModal();
  });

  return {
    bidInfoAtom,
    trackAvailableSparksClick,
    openBidModalWithTextLink,
    openBidModalWithButton,
  };
});

export const winnerAnnouncementBunja = bunja(() => {
  const { bidInfoAtom, sparkAuctionAtom } = bunja.use(sparkAuctionBunja);
  const { imageUrlAtom } = bunja.use(rewardImageAreaBunja);
  const rewardAtom = atom((get) => {
    const { reward } = get(sparkAuctionAtom);
    return reward;
  });

  return {
    rewardAtom,
    bidInfoAtom,
    imageUrlAtom,
  };
});

const millisecondsInDay = 86400000;

function getT(now: Date, start: Date, end: Date): number {
  const [n, s, e] = [Number(now), Number(start), Number(end)];
  const t = (n - s) / (e - s);
  return Math.max(0, Math.min(1, t));
}
